import { useState, useEffect } from 'react';
import apiClient from '../api/api';
import attendanceService from '../services/attendanceService';

export function useStudentProgress(studentId = 'current', halaqaId = null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignmentData, setAssignmentData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [completedAssignments, setCompletedAssignments] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    assignmentCompletion: 0,
    assessmentAverage: { value: 0, text: 'N/A' },
    attendanceRate: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch assignments data
        await fetchAssignments();
        
        // Fetch attendance data
        await fetchAttendance();
        
        setError(null);
      } catch (err) {
        console.error('Error fetching progress data:', err);
        setError('Failed to load progress data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [studentId, halaqaId]);

  useEffect(() => {
    // This effect will run only when both assignment and attendance data are updated
    if (assignmentData && attendanceData) {
      console.log("Both data sources ready - calculating metrics", {
        assignmentsCount: assignmentData.length,
        attendanceCount: attendanceData.length
      });
      calculateMetrics(assignmentData, attendanceData);
    }
  }, [assignmentData, attendanceData]);

  const fetchAssignments = async () => {
    try {
      // Different endpoint based on user role
      let endpoint;
      
      const userData = localStorage.getItem('user');
      const userObj = userData ? JSON.parse(userData) : null;
      const userRole = userObj ? userObj.role : 'student'; // Default to student if no role found
      
      let params = {};
      
      if (studentId === 'current') {
        // Current user viewing their own data - use direct student endpoint
        endpoint = '/assignments/student';
      } else {
        // Viewing someone else's data - use role-specific endpoints
        if (userRole === 'parent') {
          endpoint = `/parents/children/${studentId}/assignments`;
        } else if (userRole === 'teacher' || userRole === 'admin' || userRole === 'super_admin') {
          // Teachers/admins should use the teacher-specific endpoint
          endpoint = `/assignments/by-student/${studentId}`;
          
          // For teacher role, add halaqaId filter to get class-specific assignments
          if (halaqaId && userRole === 'teacher') {
            params.halaqaId = halaqaId;
            console.log(`Filtering teacher assignment view by class: ${halaqaId}`);
          }
        } else {
          // Fallback to student endpoint
          endpoint = '/assignments/student';
        }
      }
      
      console.log(`Using assignments endpoint: ${endpoint} for role: ${userRole}`, params);
      const response = await apiClient.get(endpoint, { params });

      // Handle different API response formats
      const assignmentsData = response.data.data || response.data || [];
      
      if (!assignmentsData || !Array.isArray(assignmentsData)) {
        console.warn('Unexpected assignments data format:', assignmentsData);
        setAssignmentData([]);
        setActiveAssignments([]);
        setCompletedAssignments([]);
        calculateMetrics([], attendanceData);
        return;
      }
      
      setAssignmentData(assignmentsData);
      
      // Filter active vs completed assignments - check both status and assessment
      const active = assignmentsData.filter(assignment => 
        assignment.status === 'active' || 
        (assignment.assessment && assignment.assessment.rating === 'Need to Repeat')
      );

      // Get the completed assignments - must be completed status AND not need repeat
      const completed = assignmentsData
        .filter(assignment => 
          assignment.status === 'completed' && 
          (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
        )
        .sort((a, b) => {
          // Sort by assessment date if available
          if (a.assessment?.assessedAt && b.assessment?.assessedAt) {
            return new Date(b.assessment.assessedAt) - new Date(a.assessment.assessedAt);
          }
          return 0;
        })
        .slice(0, 5);
      
      setActiveAssignments(active);
      setCompletedAssignments(completed);
      
    } catch (error) {
      console.error('Error fetching assignments for dashboard:', error);
      setAssignmentData([]);
    }
  };

  const fetchAttendance = async () => {
    try {
      let attendanceRecords = [];
      const userData = localStorage.getItem('user');
      const userObj = userData ? JSON.parse(userData) : null;
      const userRole = userObj ? userObj.role : 'student';
      const userId = userObj ? (userObj.id || userObj._id) : null;
      
      if (studentId === 'current') {
        // Current user viewing their own data - use attendanceService
        attendanceRecords = await attendanceService.getStudentAttendance(userId, halaqaId);
      } else {
        // Role-specific endpoints
        if (userRole === 'parent') {
          // Parent endpoint - still use direct API for now as it may have different structure
          const endpoint = `/parents/children/${studentId}/attendance`;
          console.log(`Using attendance endpoint: ${endpoint} for role: ${userRole}`);
          
          // Build query parameters for parent endpoint
          const params = {};
          if (halaqaId) {
            params.halaqaId = halaqaId;
            console.log(`Filtering parent attendance view by class: ${halaqaId}`);
          }
          
          const response = await apiClient.get(endpoint, { params });
          attendanceRecords = response.data.data || [];
        } else {
          // Teachers and admins use attendanceService with halaqaId filtering
          attendanceRecords = await attendanceService.getStudentAttendance(studentId, halaqaId);
        }
      }
      
      console.log(`Received ${attendanceRecords.length} attendance records${halaqaId ? ` filtered by class ${halaqaId}` : ''}`);
      setAttendanceData(attendanceRecords);
    } catch (error) {
      console.error('Error fetching attendance for dashboard:', error);
      setAttendanceData([]);
    }
  };

  const calculateMetrics = (assignmentsData, attendanceData) => {
    if (!assignmentsData) assignmentsData = [];
    if (!attendanceData) attendanceData = [];
    
    // Calculate assignment completion rate
    const totalAssignments = assignmentsData.length;
    const completedAssignments = assignmentsData.filter(assignment => 
      assignment.status === 'completed' &&
      (!assignment.assessment || assignment.assessment.rating !== 'Need to Repeat')
    ).length;
    
    const completionRate = totalAssignments > 0 
      ? Math.round((completedAssignments / totalAssignments) * 100) 
      : 0;
    
    // Calculate assessment average
    const assessedAssignments = assignmentsData.filter(assignment => 
      assignment.assessment && assignment.assessment.rating
    );
    
    let assessmentAvgValue = 0;
    let assessmentText = 'N/A';
    
    if (assessedAssignments.length > 0) {
      // Map ratings to numerical values
      const ratingValues = {
        'Excellent': 5,
        'Very Good': 4,
        'Good': 3,
        'Pass': 2,
        'Need to Repeat': 1
      };
      
      const assessmentSum = assessedAssignments.reduce((sum, assignment) => {
        return sum + (ratingValues[assignment.assessment.rating] || 0);
      }, 0);
      
      assessmentAvgValue = assessmentSum / assessedAssignments.length;
      
      // Determine text rating based on average
      if (assessmentAvgValue >= 4.5) assessmentText = 'Excellent';
      else if (assessmentAvgValue >= 3.5) assessmentText = 'Very Good';
      else if (assessmentAvgValue >= 2.5) assessmentText = 'Good';
      else if (assessmentAvgValue >= 1.5) assessmentText = 'Pass';
      else assessmentText = 'Need to Repeat';
    }
    
    // Calculate attendance rate
    let attendanceRate = 0;
    
    if (Array.isArray(attendanceData) && attendanceData.length > 0) {
      const totalSessions = attendanceData.length;
      const attendedSessions = attendanceData.filter(record => 
        record.status?.toLowerCase() === 'present' || record.status?.toLowerCase() === 'late'
      ).length;
      
      attendanceRate = totalSessions > 0 
        ? Math.round((attendedSessions / totalSessions) * 100) 
        : 0;
    } else if (attendanceData && attendanceData.stats && attendanceData.stats.attendanceRate) {
      // If stats are provided directly (parent endpoint)
      attendanceRate = parseInt(attendanceData.stats.attendanceRate.replace('%', '')) || 0;
    }
    
    // Update stats
    setDashboardStats({
      assignmentCompletion: completionRate,
      assessmentAverage: { 
        value: assessmentAvgValue > 0 ? parseFloat(assessmentAvgValue.toFixed(1)) : 0, 
        text: assessmentText 
      },
      attendanceRate: attendanceRate,
      // Additional details for display consistency with admin/parent views
      completedAssignments: completedAssignments,
      totalAssignments: totalAssignments,
      assessedAssignments: assessedAssignments.length,
      presentDays: Array.isArray(attendanceData) ? attendanceData.filter(record => 
        record.status?.toLowerCase() === 'present' || record.status?.toLowerCase() === 'late'
      ).length : 0,
      totalDays: Array.isArray(attendanceData) ? attendanceData.length : 0
    });
  };

  return {
    loading,
    error,
    dashboardStats,
    activeAssignments,
    completedAssignments,
    assignmentData,
    attendanceData,
    refreshData: async () => {
      setLoading(true);
      try {
        await fetchAssignments();
        await fetchAttendance();
        setError(null);
      } catch (err) {
        console.error('Error refreshing data:', err);
        setError('Failed to refresh data');
      } finally {
        setLoading(false);
      }
    }
  };
}

export default useStudentProgress;